import {
  AccessType,
  type Dependency,
  type FeatureAccessResolver,
  type MembershipLevel,
} from '@onxmaps/feature-access'
import { FishClaims } from '@/services/feature-access/fish/types'
import { Vertical } from '@/types/verticals'

// Access to species detail information
/**
 * Resolves access to fish species detail based on claims and membership level.
 */
export const resolveFishSpeciesDetail: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.species_detail]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

/**
 * Resolves access to waterbody detail information based on claims and membership level.
 */
// Access to waterbody detail information
export const resolveWaterbodyDetail: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.waterbody_detail]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

// Access to specialty waterbody filters
/**
 * Resolves access to specialty waterbody filters based on claims and membership level.
 */
export const resolveWaterbodyFilters: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.waterbody_filters]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

// Access to waterbody access point details
/**
 * Resolves access to waterbody access point details based on claims and membership level.
 */
export const resolveAccessPoints: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.access_points]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

// Access to fishing area details
/**
 * Resolves access to fishing area detail based on claims and membership level.
 */
export const resolveFishingAreaDetail: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.fishing_area_detail]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

// Access to fishing reports
/**
 * Resolves access to fishing reports based on claims and membership level.
 */
export const resolveFishingReports: FeatureAccessResolver<
  Dependency.CLAIMS | Dependency.MEMBERSHIP
> = async ({ getClaims, getMembership }): Promise<{ type: AccessType }> => {
  const claims = await getClaims()
  const allFishClaims = claims.get(Vertical.Fish) as Record<FishClaims, boolean>
  const claim = allFishClaims?.[FishClaims.fishing_reports]
  if (claim) {
    return { type: AccessType.HAS_ACCESS }
  }

  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

/**
 * Resolves access to the premium fish content package based on claims and membership level.
 */
// Display upsell for special map layers
// Actual layer access management is done through the v2/collections endpoint `hasAccess` attribute
export const resolveMapLayers: FeatureAccessResolver<
  Dependency.MEMBERSHIP
> = async ({ getMembership }): Promise<{ type: AccessType }> => {
  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

// Access to private land data
/**
 * Resolves access to private land data based on membership level.
 */
export const resolveFishLandAccess: FeatureAccessResolver<
  Dependency.MEMBERSHIP
> = async ({ getMembership }): Promise<{ type: AccessType }> => {
  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel === 'elite' || membershipLevel === 'employee') {
    return { type: AccessType.HAS_ACCESS }
  }

  return { type: AccessType.NO_ACCESS }
}

/**
 * Resolves access to animated wind feature based on membership level.
 */
export const resolveAnimatedWind: FeatureAccessResolver<
  Dependency.MEMBERSHIP
> = async ({ getMembership }): Promise<{ type: AccessType }> => {
  const levelsWithoutAccess: MembershipLevel[] = ['basic']
  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel && !levelsWithoutAccess.includes(membershipLevel)) {
    return { type: AccessType.HAS_ACCESS }
  }
  return { type: AccessType.NO_ACCESS }
}

/**
 * Resolves access to catch data feature based on membership level
 */
export const resolveCatchData: FeatureAccessResolver<
  Dependency.MEMBERSHIP | Dependency.FEATURE_FLAG_CLIENTS
> = async ({ getMembership }): Promise<{ type: AccessType }> => {
  const levelsWithoutAccess: MembershipLevel[] = ['basic']
  const membership = await getMembership()
  const membershipLevel = membership.get(Vertical.Fish)?.membership_level

  if (membershipLevel && !levelsWithoutAccess.includes(membershipLevel)) {
    return { type: AccessType.HAS_ACCESS }
  }
  return { type: AccessType.NO_ACCESS }
}
